/**
 *  HistogramChart script file
**/
import * as d3 from 'd3'

export default {
  name: 'HistogramChart',
  components: {},
  props: {
    chartData: {
      type: Array,
      required: true
    },
    rotateTicks: {
      type: Boolean,
      default: false
    },
    tickInterval: {
      type: Number,
      default: 2,
      required: false
    },
    color: {
      type: String,
      required: false,
      default: '#17969d'
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    labelLeft: {
      type: String,
      default: undefined,
      required: false
    },
    legend: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      tooltipData: {
        key: '',
        value: ''
      },
      tooltipClass: ''
    }
  },
  mounted () {
    window.setTimeout(() => {
      this.drawChart()
    })
  },
  computed: {
  },
  methods: {
    drawChart () {
      const wrapper = this.$refs.wrapper.getBoundingClientRect()
      const margin = { top: 20, right: 20, bottom: 20, left: 60 }
      const width = wrapper.width - margin.left - margin.right
      const height = wrapper.height - margin.top - margin.bottom

      const radX = 2
      const radY = 2

      const svg = d3.select(this.$refs.svg)
        .attr('width', `${wrapper.width}px`)
        .attr('height', `${wrapper.height}px`)

      const g = svg.append('g')
        .attr('transform', `translate(0, ${margin.top})`)

      const xScaleGroup = d3.scaleBand()
        .rangeRound([0, wrapper.width - margin.left - 20])
        .paddingInner(0.01)

      // column padding
      const xScaleBar = d3.scaleBand()
        .paddingInner(0.7)

      const yScale = d3.scaleLinear()
        .rangeRound([height, 0])

      const colors = ['#17969d']
      const colorScale = d3.scaleOrdinal()
        .range(colors)

      const keys = Object.keys(this.chartData[0].values)

      xScaleGroup.domain(this.chartData.map(d => d.name))
      xScaleBar.domain(keys).rangeRound([0, xScaleGroup.bandwidth()])
      yScale.domain([0, d3.max(this.chartData, (d) => d3.max(keys, (k) => d.values[k]))])

      g.append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${margin.left + 4}, 0)`)
        .call(
          d3.axisLeft(yScale)
            .ticks(4)
            .tickSize(-(width + margin.left))
        )

      g.append('g')
        .attr('transform', `translate(${margin.left + 10}, 0)`)
        .attr('class', 'bars')
        .selectAll('g')
        .data(this.chartData)
        .enter().append('g')
        .attr('class', 'bar')
        .attr('transform', (d) => `translate(${xScaleGroup(d.name)}, 0)`)
        .on('mouseover', d => this.showTooltip(d, colorScale))
        .on('mousemove', d => this.moveTooltip())
        .on('mouseout', d => this.hideTooltip())
        .selectAll('path')
        .data((d) => keys.map(key => ({ key: key, value: d.values[key] })))
        .enter().append('path')
        .attr('d', d => `
          M${xScaleBar(d.key)},${yScale(d.value) + (d.value <= 2 ? 0 : radY)}
          a${radX},${radY} 0 0 1 ${radX},${d.value <= 2 ? 0 : -radY}
          h${xScaleBar.bandwidth() - 2 * radX}
          a${radX},${radY} 0 0 1 ${radX},${d.value <= 2 ? 0 : radY}
          v${height - yScale(d.value) - (d.value <= 2 ? 0 : radY)}
          h${-(xScaleBar.bandwidth())}Z
        `)
        .attr('fill', (d) => colorScale(d.key))
        .style('opacity', 0.85)

      const xValues = this.chartData.map(d => d.name)

      const axisBottom = g.append('g')
        .attr('class', 'axis axis-x')
        .attr('transform', `translate(${margin.left + 10}, ${height})`)
        .call(d3.axisBottom(xScaleGroup).tickValues(xValues))

      if (this.rotateTicks) {
        axisBottom.selectAll('text')
          .attr('y', 0)
          .attr('x', xScaleBar.bandwidth() / 2)
          .attr('dy', '1.5em')
          .style('text-anchor', 'end')
          .attr('transform', 'rotate(-30)')
      }

      g.append('g')
        .attr('class', 'axis axis-y')
        .attr('transform', `translate(${margin.left + 8}, 0)`)
        .call(d3.axisLeft(yScale).ticks(4))

      d3.selectAll('.grid .tick line').style('stroke', '#f2f2f4')
      d3.selectAll('.grid .tick text').style('display', 'none')
      d3.selectAll('path.domain').style('display', 'none')
      d3.selectAll('.axis .tick line').style('display', 'none')
      d3.selectAll('.axis .tick text').style('fill', '#383d4c')
        .style('font-family', '"OpenSans", sans-serif')
        .style('font-size', '10px')
        .style('font-weight', '600')
      d3.selectAll('.axis-y .tick text').style('fill', '#9397a2')
    },
    showTooltip (data, colorScale) {
      this.tooltipData = {
        label: data.name,
        value: data.values[0],
        color: colorScale(0)
      }

      this.$refs.tooltip.style.opacity = 1
    },
    moveTooltip () {
      const bcr = this.$refs.wrapper.getBoundingClientRect()
      const x = d3.event.pageX - bcr.x

      this.$refs.tooltip.style.left = `${x}px`
    },
    hideTooltip () {
      this.$refs.tooltip.style.opacity = 0
    }
  }
}
